import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, ShieldCheck, GraduationCap, Printer, Package, Tag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { listProducts, listCategories, listBundles, getReviewsSummary } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BundleCard from "@/components/BundleCard";
import { HeroSlideshow } from "@/components/HeroSlideshow";

export default function Home() {
  const { t, lang } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    listProducts({ featured: true }).then(setFeatured).catch(() => {});
    listCategories().then((r) => setCategories(r.categories)).catch(() => {});
    listBundles({ featured: true }).then(setBundles).catch(() => {});
    getReviewsSummary().then(setRatings).catch(() => setRatings({}));
  }, []);

  const features = [
    { icon: Download, label: t("feat_1") },
    { icon: ShieldCheck, label: t("feat_2") },
    { icon: GraduationCap, label: t("feat_3") },
    { icon: Printer, label: t("feat_4") },
  ];

  return (
    <>
      {/* HERO — Animated slideshow */}
      <HeroSlideshow products={featured} />

      {/* Features Strip */}
      <div className="border-y border-[#E5E2DC] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#E5E2DC]">
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-5 md:py-6 md:px-8 first:pl-0 last:pr-0"
              >
                <f.icon className="w-5 h-5 text-[#8BA896]" strokeWidth={1.5} />
                <p className="text-sm text-[#2C302E] font-medium">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-[#D97B66]"></span>
              <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
                {t("featured_eyebrow")}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] leading-tight">
              {t("featured_title")}
            </h2>
            <p className="mt-4 text-[#6B726C] text-base md:text-lg">
              {t("featured_sub")}
            </p>
          </div>
          <Link
            to="/butikk"
            data-testid="featured-view-all"
            className="text-sm text-[#2C302E] hover:text-[#D97B66] transition-colors flex items-center gap-2 group whitespace-nowrap"
          >
            {t("shop_all")}
            <ArrowRight
              className="w-4 h-4 group-hover:translate-x-1 transition-transform"
              strokeWidth={1.5}
            />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.slice(0, 6).map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              idx={i}
              rating={ratings[p.slug] || null}
            />
          ))}
        </div>
      </section>

      {/* BUNDLES */}
      {bundles.length > 0 && (
        <section className="bg-gradient-to-b from-[#F9F8F6] to-white py-20 md:py-32 border-y border-[#E5E2DC]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-px bg-[#D97B66]"></span>
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium inline-flex items-center gap-2">
                    <Package className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {t("bundles_eyebrow")}
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] leading-tight">
                  {t("bundles_title")}
                </h2>
                <p className="mt-4 text-[#6B726C] text-base md:text-lg">
                  {t("bundles_sub")}
                </p>
              </div>
              <Link
                to="/pakker"
                data-testid="bundles-view-all"
                className="text-sm text-[#2C302E] hover:text-[#D97B66] transition-colors flex items-center gap-2 group whitespace-nowrap"
              >
                {t("bundle_view_all")}
                <ArrowRight
                  className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                  strokeWidth={1.5}
                />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {bundles.slice(0, 3).map((b, i) => (
                <BundleCard key={b.id} bundle={b} idx={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="bg-[#2C302E] text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-[#D97B66]"></span>
              <span className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium">
                {t("categories_eyebrow")}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-light leading-tight">
              {t("categories_title")}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {categories.map((c, i) => (
              <Link
                key={c.slug}
                to={`/shop?category=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group block border border-white/10 hover:border-[#D97B66] rounded-xl p-6 md:p-8 transition-all hover:bg-white/5"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">
                  0{i + 1}
                </p>
                <p className="font-heading text-lg md:text-xl font-medium text-white">
                  {lang === "en" ? c.name_en : c.name_no}
                </p>
                <div className="mt-6 flex items-center gap-2 text-[#D97B66] text-sm">
                  <span>{lang === "en" ? "Browse" : "Utforsk"}</span>
                  <ArrowRight
                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    strokeWidth={1.5}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-32 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#E5E2DC]">
          <img
            src="https://images.pexels.com/photos/7220426/pexels-photo-7220426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              {t("about_eyebrow")}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] leading-tight">
            {t("about_title")}
          </h2>
          <p className="mt-6 text-[#6B726C] text-base md:text-lg leading-relaxed">
            {t("about_text")}
          </p>
          <ul className="mt-8 space-y-3">
            {[t("about_point_1"), t("about_point_2"), t("about_point_3")].map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-[#2C302E]">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#D97B66] flex-shrink-0"></span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
